import styled, { css, keyframes } from 'styled-components';
import { Text } from '~/components/ui/Text';
import { RewardType } from '~/shared';
import shadowCenter from '../../assets/images/shop_open_case/case_center_shadow.png';
import centerArrows from '../../assets/images/shop_open_case/center_arrows.svg';
import light from '../../assets/images/shop_open_case/light.png';
import rewardShadow from '../../assets/images/shop_open_case/open_case_result_shadow.png';
import shadow from '../../assets/images/shop_open_case/open_case_shadow.png';
import starBg from '../../assets/images/shop_open_case/open_case_star_bg.svg';
import secretReward from '../../assets/images/shop_open_case/secret_reward.png';

const glowPulse = keyframes`
	0% {
		opacity: var(--glow-o-min, 0.6);
		filter: blur(var(--glow-blur-min, 4px)) saturate(1);
	}
	100% {
		opacity: var(--glow-o-max, 1);
		filter: blur(var(--glow-blur-max, 10px)) saturate(1.2);
	}
`;

const rewardPulse = keyframes`
	0% {
		transform: translate(-50%, -50%) scale(var(--reward-scale-min, 0.98));
		opacity: var(--reward-o-min, 0.65);
	}
	100% {
		transform: translate(-50%, -50%) scale(var(--reward-scale-max, 1.03));
		opacity: var(--reward-o-max, 1);
	}
`;

const lightSweep = keyframes`
	0% {
		left: -180px;
	}
	100% {
		left: 100%;
	}
`;

const StyledLight = styled.div`
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	left: -180px;
	z-index: 3;
	width: 180px;
	height: 420px;
	background-image: url(${light});
	background-size: 180px 420px;
	background-repeat: no-repeat;
	background-position: center;
	pointer-events: none;
	animation: ${lightSweep} 3s linear infinite;
`;

const StyledContainer = styled.div`
	position: relative;
	height: 500px;
	min-height: 500px;
	display: flex;
	align-items: center;
	justify-content: center;
	/* border-top: 1px solid rgba(255, 255, 255, 0.1); */
	transition:
		opacity 0.4s ease,
		transform 0.4s ease;

	&.case-opening-enter {
		opacity: 0;
		transform: translateY(20px);
	}

	&.case-opening-enter-active {
		opacity: 1;
		transform: translateY(0);
	}

	&.case-opening-enter-done {
		opacity: 1;
		transform: translateY(0);
	}

	&.case-opening-exit {
		opacity: 1;
		transform: translateY(0);
	}

	&.case-opening-exit-active {
		opacity: 0;
		transform: translateY(-20px);
	}
`;

const StyledShadow = styled.div<{ $animate?: boolean }>`
	position: absolute;
	top: -270px;
	left: -270px;
	z-index: 2;
	width: 540px;
	height: 540px;
	mask-image: url(${shadow});
	mask-size: 540px 540px;
	mask-repeat: no-repeat;
	mask-position: center;
	-webkit-mask-image: url(${shadow});
	-webkit-mask-size: 540px 540px;
	-webkit-mask-repeat: no-repeat;
	-webkit-mask-position: center;
	pointer-events: none;

	${(props) =>
		props.$animate &&
		css`
			will-change: opacity, filter;
			animation: ${glowPulse} var(--glow-dur, 2.2s) ease-in-out
				var(--glow-delay, 0s) infinite alternate;
		`}
`;

const StyledShadowCenter = styled.div<{ $animate?: boolean }>`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	z-index: 1;
	width: 500px;
	height: 500px;
	mask-image: url(${shadowCenter});
	mask-size: 500px 500px;
	mask-repeat: no-repeat;
	mask-position: center;
	-webkit-mask-image: url(${shadowCenter});
	-webkit-mask-size: 500px 500px;
	-webkit-mask-repeat: no-repeat;
	-webkit-mask-position: center;
	background-color: #ffffff;
	pointer-events: none;

	${(props) =>
		props.$animate &&
		css`
			will-change: opacity, filter;
			animation: ${glowPulse} var(--glow-dur, 2s) ease-in-out
				var(--glow-delay, 0s) infinite alternate;
		`}
`;

const StyledStarBg = styled.div<{ $animate?: boolean }>`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	z-index: 1;
	width: 300px;
	height: 300px;
	background-image: url(${starBg});
	background-size: 300px 300px;
	background-repeat: no-repeat;
	background-position: center;

	${(props) =>
		props.$animate &&
		css`
			will-change: opacity, filter;
			animation: ${glowPulse} var(--glow-dur, 1.8s) ease-in-out
				var(--glow-delay, 0s) infinite alternate;
		`}
`;

const StyledItemsContainer = styled.div`
	position: absolute;
	top: 120px;
	left: 50%;
	display: flex;
	gap: 10px;
	z-index: 4;
	align-items: center;
	pointer-events: none;
	will-change: transform;
	backface-visibility: hidden;
`;

const StyledItem = styled.div<{ $isDimmed?: boolean; $borderColor?: string }>`
	position: relative;
	width: 380px;
	min-width: 380px;
	height: 260px;
	background: #0c0c0c;
	border: 1px solid
		${({ $borderColor }) => $borderColor ?? 'rgba(255, 255, 255, 0.1)'};
	border-radius: 5px;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	transition:
		opacity 0.3s ease,
		border-color 0.3s ease;
	transform-style: preserve-3d;
	contain: layout style paint;

	${(props) =>
		props.$isDimmed &&
		css`
			opacity: 0.1;
		`}
`;

const StyledFlipContainer = styled.div<{ $isFlipped?: boolean }>`
	width: 380px;
	min-width: 380px;
	height: 260px;
	perspective: 1000px;
	position: relative;
	transform-style: preserve-3d;
	contain: layout style paint;

	.flip-inner {
		position: relative;
		width: 100%;
		height: 100%;
		transition: transform 0.6s;
		transform-style: preserve-3d;
		transform: ${(props) =>
			props.$isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'};
	}

	.flip-front,
	.flip-back {
		position: absolute;
		width: 100%;
		height: 100%;
		backface-visibility: hidden;
		-webkit-backface-visibility: hidden;
	}

	.flip-front {
		transform: rotateY(0deg);
	}

	.flip-back {
		transform: rotateY(180deg);
	}
`;

const StyledSecretReward = styled.div`
	position: relative;
	width: 380px;
	min-width: 380px;
	height: 260px;
	border-radius: 5px;
	background-image: url(${secretReward});
	background-size: 380px 260px;
	background-repeat: no-repeat;
	background-position: center;
`;

const StyledCount = styled.div`
	position: absolute;
	top: 40px;
	left: 50%;
	transform: translateX(-50%);
	display: flex;
	align-items: center;
	justify-content: center;
	height: 40px;
	padding: 0 10px;
	background: rgba(255, 255, 255, 0.02);
	border: 1px solid rgba(255, 255, 255, 0.05);
	border-radius: 10px;

	${Text} {
		color: #ffffff;
	}
`;

const StyledItemTopContainer = styled.div`
	position: absolute;
	top: 20px;
	left: 20px;
	display: flex;
	align-items: center;
	gap: 10px;
	z-index: 3;

	.dot {
		width: 10px;
		height: 10px;
		border-radius: 10px;
	}

	${Text} {
		font-weight: 500;
		font-size: 16px;
		color: #ffffff;
	}
`;

const StyledImageContainer = styled.div<{ $type: RewardType }>`
	position: absolute;
	left: 50%;
	transform: translateX(-50%);
	z-index: 3;

	${(props) =>
		props.$type === RewardType.VEHICLE &&
		css`
			top: 76px;
		`}

	${(props) =>
		props.$type === RewardType.ITEM &&
		css`
			top: 50px;
		`}

		${(props) =>
		props.$type === RewardType.MONEY &&
		css`
			top: 50px;
		`}

		${(props) =>
		props.$type === RewardType.EXP &&
		css`
			top: 110px;
		`}

	img {
		user-select: none;
		pointer-events: none;
	}
`;

const StyledButtonContent = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;

	img {
		width: 20px;
		height: 20px;
	}
`;

const StyledButtonContainer = styled.div`
	position: absolute;
	top: 420px;
	left: 50%;
	transform: translateX(-50%);
	z-index: 4;
`;

const StyledRewardButtonsGrid = styled.div<{ $canBeSold: boolean }>`
	display: grid;
	grid-template-columns: ${({ $canBeSold }) =>
		$canBeSold ? '240px 240px 240px' : '240px 240px'};
	gap: 20px;
`;

const StyledRewardShadow = styled.div<{ $animate?: boolean }>`
	position: absolute;
	top: calc(50% + 20px);
	left: 50%;
	transform: translate(-50%, -50%);
	z-index: 2;
	width: 900px;
	height: 900px;
	mask-image: url(${rewardShadow});
	mask-size: 900px 900px;
	mask-repeat: no-repeat;
	mask-position: center;
	-webkit-mask-image: url(${rewardShadow});
	-webkit-mask-size: 900px 900px;
	-webkit-mask-repeat: no-repeat;
	-webkit-mask-position: center;
	pointer-events: none;
	transition: opacity 0.3s ease;
	transform-origin: 50% 50%;
	will-change: opacity, transform;

	${(props) =>
		props.$animate &&
		css`
			animation: ${rewardPulse} var(--reward-dur, 2.6s) ease-in-out
				var(--reward-delay, 0s) infinite alternate;
		`}
`;

const StyledCenterArrows = styled.div`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	z-index: 5;
	width: 40px;
	height: 288px;
	background-image: url(${centerArrows});
	background-size: 40px 288px;
	background-repeat: no-repeat;
	background-position: center;
	transition: opacity 0.3s ease;
`;

const StyledBackButtonContainer = styled.div`
	position: absolute;
	left: 40px;
	top: 19px;
`;

const StyledExpContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;

	${Text} {
		font-weight: 700;
		font-size: 30px;
		color: #ffffff;
	}
`;

export {
	StyledBackButtonContainer,
	StyledButtonContainer,
	StyledButtonContent,
	StyledCenterArrows,
	StyledContainer,
	StyledCount,
	StyledExpContainer,
	StyledFlipContainer,
	StyledImageContainer,
	StyledItem,
	StyledItemsContainer,
	StyledItemTopContainer,
	StyledLight,
	StyledRewardButtonsGrid,
	StyledRewardShadow,
	StyledSecretReward,
	StyledShadow,
	StyledShadowCenter,
	StyledStarBg,
};
